import type { HexMapState } from '../../engine/render/renderer';
import {
    getFreePopulation,
    getHouseCapacity,
    getPopulationCount,
} from '../../engine/map/settlement';
import {
    ACTION__MODAL__TAB__BUILDINGS,
    ACTION__MODAL__TAB__BUILDINGS__CORE,
    ACTION__MODAL__TAB__BUILDINGS__FOOD,
    ACTION__MODAL__TAB__BUILDINGS__INDUSTRY,
    ACTION__MODAL__TAB__BUILDINGS__MINING,
    ACTION__MODAL__TAB__BUILDINGS__CRAFT,
    ACTION__MODAL__TAB__BUILDINGS__SUPPORT,
    ACTION__MODAL__TAB__FOREST,
    ACTION__MODAL__TAB__NATURE,
    ACTION__MODAL__TAB__JOBS,
    ACTION__MODAL__TAB__JOBS__CORE,
    ACTION__MODAL__TAB__JOBS__FOOD,
    ACTION__MODAL__TAB__JOBS__INDUSTRY,
    ACTION__MODAL__TAB__JOBS__MINING,
    ACTION__MODAL__TAB__JOBS__CRAFT,
    ACTION__MODAL__TAB__JOBS__SUPPORT,
    ACTION__MODAL__TAB__LOG,
    ACTION__MODAL__TAB__POPULATION,
    ACTION__MODAL__TAB__PRODUCTION,
    ACTION__MODAL__TAB__STORAGE,
    ACTION__MODAL__TAB__TOOLS,
    ACTION__MODAL__TAB__WILDLIFE,
    ACTION__SETTLEMENT__SET_HOUSE_FOOD_TARGET,
    ACTION__SETTLEMENT__SET_HOUSE_HEAT_TARGET,
    ACTION__SETTLEMENT__SET_BLACKSMITH_TOOL_TARGET,
    ACTION__SETTLEMENT__SET_BLACKSMITH_WEAPON_TARGET,
    ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX,
    ACTION__SETTLEMENT__SELECT_HOUSE_PREFIX,
    ACTION__MODAL__TAB__ASSISTANTS,
} from '../../engine/input/actionIds';
import type { ContentBuilder } from '../ui/ContentBuilder';
import { AbstractModal } from '../AbstractModal';
import { renderBuildingsTab } from './tabs/TabBuildings';
import { renderJobsTab } from './tabs/TabJobs';
import { renderProductionTab } from './tabs/TabProduction';
import { renderLogTab } from './tabs/TabLog';
import { renderForestTab } from './tabs/TabForest';
import { renderNatureTab } from './tabs/TabNature';
import { renderToolsTab } from './tabs/TabTools';
import { renderWildlifeTab } from './tabs/TabWildlife';
import { renderStorageTab } from './tabs/TabStorage';
import { renderPopulationTab } from './tabs/TabPopulation';
import { t } from '../../i18n/translations';
import {
    BuildingProductionModal,
    isProductionBuildingId,
} from '../production/BuildingProductionModal';
import { renderAssistantsTab } from './tabs/TabAssistants';

export class SettlementModal extends AbstractModal {
    private activeBuildingsTabId: string | null = null;
    activeJobsTabId: string | null = null;
    private selectedHouseId: string | null = null;

    getHeaderLabel(_state: HexMapState): string {
        return t(_state.config.ui.language, 'settlement.title');
    }

    handleAction(actionId: string, state: HexMapState): boolean {
        switch (actionId) {
            case ACTION__MODAL__TAB__BUILDINGS:
            case ACTION__MODAL__TAB__JOBS:
            case ACTION__MODAL__TAB__PRODUCTION:
            case ACTION__MODAL__TAB__LOG:
            case ACTION__MODAL__TAB__FOREST:
            case ACTION__MODAL__TAB__NATURE:
            case ACTION__MODAL__TAB__POPULATION:
            case ACTION__MODAL__TAB__TOOLS:
            case ACTION__MODAL__TAB__WILDLIFE:
            case ACTION__MODAL__TAB__STORAGE:
            case ACTION__MODAL__TAB__ASSISTANTS:
                this.activeTabId = actionId;
                this.scroll = 0;
                return true;
            case ACTION__MODAL__TAB__BUILDINGS__CORE:
            case ACTION__MODAL__TAB__BUILDINGS__FOOD:
            case ACTION__MODAL__TAB__BUILDINGS__INDUSTRY:
            case ACTION__MODAL__TAB__BUILDINGS__MINING:
            case ACTION__MODAL__TAB__BUILDINGS__CRAFT:
            case ACTION__MODAL__TAB__BUILDINGS__SUPPORT:
                this.activeBuildingsTabId = actionId;
                this.scroll = 0;
                return true;
            case ACTION__MODAL__TAB__JOBS__CORE:
            case ACTION__MODAL__TAB__JOBS__FOOD:
            case ACTION__MODAL__TAB__JOBS__INDUSTRY:
            case ACTION__MODAL__TAB__JOBS__MINING:
            case ACTION__MODAL__TAB__JOBS__CRAFT:
            case ACTION__MODAL__TAB__JOBS__SUPPORT:
                this.activeJobsTabId = actionId;
                this.scroll = 0;
                return true;
            case ACTION__SETTLEMENT__SET_HOUSE_FOOD_TARGET: {
                const settlement = this.settlementId
                    ? state.settlementsById[this.settlementId]
                    : null;
                if (!settlement) return false;
                const current = settlement.production.population.houseFoodTargetHours;
                const next = window.prompt(
                    t(state.config.ui.language, 'prompt.houseFoodTarget'),
                    `${current}`,
                );
                if (next === null) return false;
                const parsed = Number(next);
                if (!Number.isFinite(parsed)) return false;
                const value = Math.max(1, Math.floor(parsed));
                settlement.production.population.houseFoodTargetHours = value;
                return true;
            }
            case ACTION__SETTLEMENT__SET_HOUSE_HEAT_TARGET: {
                const settlement = this.settlementId
                    ? state.settlementsById[this.settlementId]
                    : null;
                if (!settlement) return false;
                const current = settlement.production.population.houseHeatTargetHours;
                const next = window.prompt(
                    t(state.config.ui.language, 'prompt.houseHeatTarget'),
                    `${current}`,
                );
                if (next === null) return false;
                const parsed = Number(next);
                if (!Number.isFinite(parsed)) return false;
                const value = Math.max(1, Math.floor(parsed));
                settlement.production.population.houseHeatTargetHours = value;
                return true;
            }
            case ACTION__SETTLEMENT__SET_BLACKSMITH_TOOL_TARGET: {
                const settlement = this.settlementId
                    ? state.settlementsById[this.settlementId]
                    : null;
                if (!settlement) return false;
                const current = settlement.production.blacksmith.targetTools;
                const next = window.prompt(
                    t(
                        state.config.ui.language,
                        'prompt.blacksmithToolsTarget',
                        t(state.config.ui.language, 'resource.tools'),
                    ),
                    `${current}`,
                );
                if (next === null) return false;
                const parsed = Number(next);
                if (!Number.isFinite(parsed)) return false;
                settlement.production.blacksmith.targetTools = Math.max(0, Math.floor(parsed));
                return true;
            }
            case ACTION__SETTLEMENT__SET_BLACKSMITH_WEAPON_TARGET: {
                const settlement = this.settlementId
                    ? state.settlementsById[this.settlementId]
                    : null;
                if (!settlement) return false;
                const current = settlement.production.blacksmith.targetWeapons;
                const next = window.prompt(
                    t(
                        state.config.ui.language,
                        'prompt.blacksmithWeaponsTarget',
                        t(state.config.ui.language, 'resource.weapons'),
                    ),
                    `${current}`,
                );
                if (next === null) return false;
                const parsed = Number(next);
                if (!Number.isFinite(parsed)) return false;
                settlement.production.blacksmith.targetWeapons = Math.max(0, Math.floor(parsed));
                return true;
            }
            default:
                if (actionId.startsWith(ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX)) {
                    const buildingId = actionId.slice(
                        ACTION__SETTLEMENT__OPEN_PRODUCTION_PREFIX.length,
                    );
                    if (!this.settlementId || !isProductionBuildingId(buildingId)) return false;
                    const modalId = `production_modal_${this.settlementId}_${buildingId}`;
                    const existing = state.modals.find((modal) => modal.id === modalId);
                    if (existing) return false;
                    const rect = {
                        x: this.rect.x + 20,
                        y: this.rect.y + 20,
                        w: this.rect.w,
                        h: this.rect.h,
                    };
                    state.modals.push(
                        new BuildingProductionModal(rect, this.tile, this.settlementId, buildingId),
                    );
                    state.activeModalId = modalId;
                    return true;
                }
                if (actionId.startsWith(ACTION__SETTLEMENT__SELECT_HOUSE_PREFIX)) {
                    this.selectedHouseId = actionId.slice(
                        ACTION__SETTLEMENT__SELECT_HOUSE_PREFIX.length,
                    );
                    this.activeTabId = ACTION__MODAL__TAB__POPULATION;
                    return true;
                }
                return false;
        }
    }

    buildContent(state: HexMapState, builder: ContentBuilder): void {
        const settlement = this.settlementId ? state.settlementsById[this.settlementId] : null;
        if (!settlement) return;
        const free = getFreePopulation(settlement);
        const lang = state.config.ui.language;

        if (!this.activeTabId) this.activeTabId = ACTION__MODAL__TAB__BUILDINGS;
        if (!this.activeBuildingsTabId)
            this.activeBuildingsTabId = ACTION__MODAL__TAB__BUILDINGS__CORE;
        if (!this.activeJobsTabId) this.activeJobsTabId = ACTION__MODAL__TAB__JOBS__CORE;

        builder.text(t(lang, 'settlement.city', settlement.name));
        builder.text(t(lang, 'settlement.tier', t(lang, `tier.${settlement.tier}`)));
        const popCap = settlement.houses.length * getHouseCapacity();
        builder.text(
            t(lang, 'settlement.population', getPopulationCount(settlement), popCap, free),
        );
        builder.spacer(4);

        builder.tabs(
            [
                {
                    id: ACTION__MODAL__TAB__BUILDINGS,
                    label: t(lang, 'tabs.buildings'),
                    active: this.activeTabId === ACTION__MODAL__TAB__BUILDINGS,
                },
                {
                    id: ACTION__MODAL__TAB__JOBS,
                    label: t(lang, 'tabs.jobs'),
                    active: this.activeTabId === ACTION__MODAL__TAB__JOBS,
                },
                {
                    id: ACTION__MODAL__TAB__PRODUCTION,
                    label: t(lang, 'tabs.production'),
                    active: this.activeTabId === ACTION__MODAL__TAB__PRODUCTION,
                },
                {
                    id: ACTION__MODAL__TAB__POPULATION,
                    label: t(lang, 'tabs.population'),
                    active: this.activeTabId === ACTION__MODAL__TAB__POPULATION,
                },
                {
                    id: ACTION__MODAL__TAB__FOREST,
                    label: t(lang, 'tabs.forest'),
                    active: this.activeTabId === ACTION__MODAL__TAB__FOREST,
                },
                {
                    id: ACTION__MODAL__TAB__WILDLIFE,
                    label: t(lang, 'tabs.wildlife'),
                    active: this.activeTabId === ACTION__MODAL__TAB__WILDLIFE,
                },
                {
                    id: ACTION__MODAL__TAB__NATURE,
                    label: t(lang, 'tabs.nature'),
                    active: this.activeTabId === ACTION__MODAL__TAB__NATURE,
                },
                {
                    id: ACTION__MODAL__TAB__TOOLS,
                    label: t(lang, 'tabs.tools'),
                    active: this.activeTabId === ACTION__MODAL__TAB__TOOLS,
                },
                {
                    id: ACTION__MODAL__TAB__STORAGE,
                    label: t(lang, 'tabs.storage'),
                    active: this.activeTabId === ACTION__MODAL__TAB__STORAGE,
                },
                {
                    id: ACTION__MODAL__TAB__ASSISTANTS,
                    label: t(lang, 'tabs.assistants'),
                    active: this.activeTabId === ACTION__MODAL__TAB__ASSISTANTS,
                },
                {
                    id: ACTION__MODAL__TAB__LOG,
                    label: t(lang, 'tabs.log'),
                    active: this.activeTabId === ACTION__MODAL__TAB__LOG,
                },
            ],
            this.tabScroll,
        );
        builder.spacer(6);

        if (this.activeTabId === ACTION__MODAL__TAB__BUILDINGS) {
            renderBuildingsTab(builder, state, settlement, this.activeBuildingsTabId);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__JOBS) {
            renderJobsTab(builder, settlement, free, lang, this.activeJobsTabId);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__PRODUCTION) {
            renderProductionTab(builder, state, settlement);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__POPULATION) {
            const houseIds = new Set(settlement.houses.map((house) => house.id));
            if (this.selectedHouseId && !houseIds.has(this.selectedHouseId)) {
                this.selectedHouseId = null;
            }
            renderPopulationTab(builder, state, settlement, lang, this.selectedHouseId);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__FOREST) {
            renderForestTab(builder, state, settlement);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__WILDLIFE) {
            renderWildlifeTab(builder, state, settlement);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__NATURE) {
            renderNatureTab(builder, state, settlement);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__TOOLS) {
            renderToolsTab(builder, settlement, lang);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__STORAGE) {
            renderStorageTab(builder, state, settlement, lang);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__LOG) {
            renderLogTab(builder, settlement, lang);
        }

        if (this.activeTabId === ACTION__MODAL__TAB__ASSISTANTS) {
            renderAssistantsTab(builder, settlement, lang);
        }
    }
}
